import {ChangeEvent, useEffect, useRef, useState} from 'react'
import {Breadcrumb, Button, ButtonGroup, Col, Dropdown, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'

import {useDisclourse} from '../../hooks/useDisclourse'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_COMMISSION_REPORTS_API} from '../../gql/queries/commissionReportsApiQueries'
import {toast} from 'react-toastify'
import {
  EdgeCommissionReports,
  IResCommissionReportsAPI,
  IResCreateCommissionReport,
  NodeCommissionReport,
  IResImportCommissionReport,
} from '../../types/commissionReportApi'
import {hasPermission, parseId, parsePrice} from '../../helpers'
import moment, {months} from 'moment'
import {ALL_PERMISSIONS, SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {
  UPDATE_COMMISSION_REPORT,
  DELETE_COMMISSION_REPORT,
  TOGGLE_STATUS_COMMISSION_REPORT,
  IMPORT_COMMISSION_REPORT,
} from '../../gql/mutations/commissionReportMutations'
import {PaginationSection} from '../shared/PaginationSection'
import {ModalAddCommissionReport} from './components/ModalAddCommissionReport'
import {ModalEditCommissionReport} from './components/ModalEditCommissionReport'
import {BadgeStatus} from './components/BadgeStatus'
import {node} from 'webpack'
import React from 'react'
import {ModalDeleteCommissionReport} from './components/ModalDeleteCommissionReport'
import {ModalConfirmation} from './components/ModalConfirmation'
import {useGetUser} from '../../store/userStore'
import {OffcanvasSearchCommissionDetails} from '../commissionReportsDetails/components/OffcanvasSearchCommissionDetails'
import {useUploadFile} from '../../hooks/useUploadFile'

export const CommissionReportsPage = () => {
  const permissions = useGetUser((s) => s.user?.permissions)
  const canEditCommission =
    permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_COMMISSION)
  const canAddCommission = permissions && hasPermission(permissions, ALL_PERMISSIONS.ADD_COMMISSION)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isAgente = userGroups.includes('Agente')
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const [gqlGetCommissionReports, {data: dataAllCommissionReports, loading, error, refetch}] =
    useLazyQuery<IResCommissionReportsAPI>(GET_ALL_COMMISSION_REPORTS_API, {
      fetchPolicy: 'no-cache',
    })

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate, error: errorUpdate}] =
    useMutation<IResCreateCommissionReport>(UPDATE_COMMISSION_REPORT)

  const [gqlDeleteCommissionReport] = useMutation(DELETE_COMMISSION_REPORT)

  const [gqlToggleStatusCommissionReport] = useMutation(TOGGLE_STATUS_COMMISSION_REPORT)

  const [
    importCommissionReportFile,
    {data: importResult, error: importError, loading: loadingImport},
  ] = useUploadFile<IResImportCommissionReport>()
  const importFileInputRef = useRef<HTMLInputElement>(null)

  const [importFileName, setImportFileName] = useState<string>('')

  useEffect(() => {
    if (importError) {
      const errorMessage =
        typeof importError === 'string'
          ? importError
          : (importError as unknown as {message?: string})?.message || 'Unable to import report.'
      toast.error(`Error: ${errorMessage}`)
    }
  }, [importError])

  useEffect(() => {
    const result = importResult?.importCommissionReport
    if (!result) return

    const {success, message, commissionReport, errors} = result
    if (success) {
      const displayName = commissionReport?.name || importFileName || 'commission-report.xlsx'
      toast.success(`Commission report "${displayName}" imported successfully`)
      setImportFileName('')
      refetch()
    } else {
      const errorMessage =
        errors
          ?.map((err) => err?.message)
          .filter(Boolean)
          .join(', ') ||
        message ||
        'Something went wrong during import.'
      toast.error(`Error: ${errorMessage}`)
    }
  }, [importResult, importFileName, refetch])

  const {isOpen, onClose, onOpen} = useDisclourse()

  const [infoPreview, setInfoPreview] = useState<null | NodeCommissionReport>(null)

  const {
    isOpen: isOpenDeleteCommissionReport,
    onClose: onCloseDeleteCommissionReport,
    onOpen: onOpenDeleteCommissionReport,
  } = useDisclourse()

  const [isOpenEdit, setIsOpenEdit] = useState(false)
  const [infoEdit, setInfoEdit] = useState<NodeCommissionReport | null>(null)

  const [isOpenCloseModal, setIsOpenCloseModal] = useState(false)
  const [selectedReport, setSelectedReport] = useState<NodeCommissionReport | null>(null)

  const handleClickClose = (node: NodeCommissionReport) => {
    setSelectedReport(node)
    setIsOpenCloseModal(true)
  }

  const handleConfirmClose = async () => {
    if (!selectedReport) return

    try {
      await gqlToggleStatusCommissionReport({
        variables: {
          id: parseId(selectedReport.id),
          newStatus: 'COMPLETED',
        },
      })
      toast.success('Commission Report closed successfully')
      refetch()
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setIsOpenCloseModal(false)
    }
  }

  const [isOpenReopenModal, setIsOpenReopenModal] = useState(false)

  const handleClickReopen = (node: NodeCommissionReport) => {
    setSelectedReport(node)
    setIsOpenReopenModal(true)
  }

  const handleConfirmReopen = async () => {
    if (!selectedReport) return

    try {
      await gqlToggleStatusCommissionReport({
        variables: {
          id: parseId(selectedReport.id),
          newStatus: 'DRAFT',
        },
      })
      toast.success('Commission Report reopened successfully')
      refetch()
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setIsOpenReopenModal(false)
    }
  }

  const handleImportButtonClick = () => {
    importFileInputRef.current?.click()
  }

  const handleImportFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setImportFileName(file.name)

    const formdata = new FormData()
    formdata.append('0', file, file.name)
    formdata.append('map', '{"0": ["variables.file"]}')

    const operations = {
      query: IMPORT_COMMISSION_REPORT,
      variables: {file: null},
    }

    formdata.append('operations', JSON.stringify(operations))
    toast.info(`Starting import: ${file.name}`)
    importCommissionReportFile(formdata)

    event.target.value = ''
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)

  const [isPag, setIsPag] = useState(false)
  const [page, setPage] = useState(1)

  const [dataArr, setDataArr] = useState<IResCommissionReportsAPI['allCommissionReports']['edges']>(
    []
  )

  let totalCommissionReports = dataAllCommissionReports?.allCommissionReports?.totalCount ?? 0

  const totalPages = Math.ceil(totalCommissionReports / first)
  const {start, end} = PaginationUtils.getPaginationRange(
    currentPage,
    first,
    totalCommissionReports
  )

  const allYears = dataAllCommissionReports?.allCommissionReports?.edges
    ?.map(({node}) => node.effectiveYear)
    .filter((year): year is number => typeof year === 'number')

  // Obtén los años únicos y ordénalos de mayor a menor
  const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => b - a)

  // Toma los 3 años más recientes
  const lastThreeYears = uniqueYears.slice(0, 3)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)

  const navigate = useNavigate()

  const initialFormData = {
    name: '',
    hasActiveClaims: false,
    status: '', // 'ACTIVE' | 'INACTIVE'
    user: '', // ID del usuario creador
  }

  const [formData, setFormData] = useState(initialFormData)
  const [filterMonth, setFilterMonth] = useState<string>('all')
  const [filterHasActiveClaims, setFilterHasActiveClaims] = useState<boolean | null>(null)

  const handleFilters = () => {
    const variables = {
      first: first,
      offset: (currentPage - 1) * first,
      name: formData.name || null,
      status: filterStatus === 'all' ? null : filterStatus,
      effectiveMonth: filterMonth === 'all' ? null : Number(filterMonth),
      effectiveYear: selectedYear ?? null,
      hasActiveClaims: filterHasActiveClaims,
    }

    if (formData.hasActiveClaims) {
      variables.hasActiveClaims = true
    }
    gqlGetCommissionReports({
      variables,
    })
  }

  type IFilterStatus = 'all' | 'COMPLETED' | 'DRAFT'
  const [filterStatus, setFilterStatus] = useState<IFilterStatus>('all')

  useEffect(() => {
    handleFilters()
    setCommissionReportsSelected([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetCommissionReports, currentPage])

  const [commissionReportsSelected, setCommissionReportsSelected] = useState<
    {id: string; name: string; status: string; user: string}[] | undefined
  >([])

  const isAllSelected =
    commissionReportsSelected?.length ===
    dataAllCommissionReports?.allCommissionReports?.edges?.length

  const toggleAll = () => {
    if (isAllSelected) {
      setCommissionReportsSelected([])
    } else {
      if (dataAllCommissionReports?.allCommissionReports?.edges?.length) {
        const allIds = dataAllCommissionReports.allCommissionReports.edges.map((edge) => {
          const creator = edge.node.createdBy
            ? [edge.node.createdBy?.firstName, edge.node.createdBy?.lastName]
                .filter(Boolean)
                .join(' ')
            : ''

          return {
            id: edge.node.id,
            name: edge.node.name,
            status: edge.node.status,
            user: creator,
          }
        })
        setCommissionReportsSelected(allIds)
      }
    }
  }

  const handlePolicyName = () => {
    handleFilters()
  }

  useEffect(() => {
    if (!dataAllCommissionReports) return
    const {edges} = dataAllCommissionReports.allCommissionReports
    // if (edges.length === 0) return
    if (isPag) {
      setDataArr((p) => [...p, ...edges])
    } else {
      setDataArr(edges)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllCommissionReports])

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error.message}`)
    }
  }, [error])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleUpdateCommissionReport = async (id: string, data: any) => {
    gqlCreateP({
      variables: {
        id: parseId(id),
        input: data,
      },
    })
  }
  function removeKeysDeep(obj: any, keysToRemove: string[] = ['__typename']): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => removeKeysDeep(item, keysToRemove))
    } else if (obj && typeof obj === 'object') {
      const newObj: Record<string, any> = {}
      for (const key in obj) {
        if (!keysToRemove.includes(key)) {
          newObj[key] = removeKeysDeep(obj[key], keysToRemove)
        }
      }
      return newObj
    }
    return obj
  }

  useEffect(() => {
    if (dataUpdate) {
      toast.success('Commission Report updated successfully')
      refetch()
    }
  }, [dataUpdate])
  // if ((loading || !dataAllCommissionReports) && !isPag) {
  //   return <LoadingCommissionReports />
  // }

  type GroupedCommissionReports = {
    [key: string]: EdgeCommissionReports[]
  }

  const groupByMonthYear = (items: EdgeCommissionReports[]): GroupedCommissionReports => {
    return items.reduce((acc, item) => {
      const {effectiveMonth, effectiveYear} = item.node
      const key = moment(`${effectiveYear}-${effectiveMonth}-01`).format('MMMM YYYY')

      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }, {} as GroupedCommissionReports)
  }

  const {
    isOpen: isOpenSearchDetails,
    onOpen: onOpenSearchDetails,
    onClose: onCloseSearchDetails,
  } = useDisclourse()

  const floatingTabStyle: React.CSSProperties = {
    position: 'fixed',
    top: '50%',
    right: 0,
    transform: 'translateY(-50%)',
    zIndex: 1051, // por encima del contenido
    borderRadius: '10px 0 0 10px',
    padding: '8px 12px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  }

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>Commissions</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          <Button
            size='sm'
            variant='info'
            onClick={() => navigate('/commissions/payroll-totals')}
          >
            <i className='bi bi-cash-stack fs-6' /> Payroll
          </Button>
          {canAddCommission && !isAgente && (
            <>
              <Button
                size='sm'
                onClick={() => {
                  onOpen()
                  setInfoPreview(null)
                }}
              >
                <i className='bi bi-plus fs-3' /> New Commission Report
              </Button>
              <Button
                size='sm'
                onClick={handleImportButtonClick}
                disabled={loadingImport}
              >
                {loadingImport ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className='bi bi-upload fs-6' /> Import Commission Report
                  </>
                )}
              </Button>
              <input
                ref={importFileInputRef}
                type='file'
                style={{display: 'none'}}
                accept='.xlsx,.xls'
                onChange={handleImportFileChange}
              />
            </>
          )}
        </div>
      </header>

      <Row className='commissionReports'>
        <Col className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>All Commission Reports</h3>
              <div className='filter-by-years'>
                {dataArr.length > 0 && <span className='fw-bold me-4'>Effective year:</span>}
                {lastThreeYears &&
                  lastThreeYears.length > 0 &&
                  lastThreeYears.map((year) => (
                    <Form.Check
                      key={year}
                      inline
                      label={year}
                      type='checkbox' // puedes mantener visual estilo checkbox
                      checked={selectedYear === year}
                      onChange={() => {
                        setSelectedYear(selectedYear === year ? null : year)
                      }}
                    />
                  ))}
              </div>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  disabled={false}
                  placeholder='Type your query here...'
                  aria-label="Recipient's username with two button addons"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // evita el submit automático
                      handlePolicyName() // si tienes una función como esa
                    }
                  }}
                  value={formData.name}
                />
                <Form.Select
                  value={filterStatus}
                  aria-label='Filter by status'
                  onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                >
                  <option value='all'>Filter by Status</option>
                  <option value='DRAFT'>Draft</option>
                  <option value='COMPLETED'>Completed</option>
                </Form.Select>
                <Form.Select
                  value={filterMonth}
                  aria-label='Filter by month'
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className='filter-month'
                >
                  <option value='all'>Filter by Month</option>
                  {[
                    'January',
                    'February',
                    'March',
                    'April',
                    'May',
                    'June',
                    'July',
                    'August',
                    'September',
                    'October',
                    'November',
                    'December',
                  ].map((month, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {month}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  disabled={false}
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Search
                </Button>
              </InputGroup>

              <Form.Check
                type='checkbox'
                id='hasActiveClaims'
                label='Has Active Claims'
                checked={filterHasActiveClaims || false}
                onChange={(e) => {
                  setFilterHasActiveClaims(e.target.checked ? true : null)
                }}
              />
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalCommissionReports}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th></th>
                    <th className='fw-bold'>Title</th>
                    <th className='fw-bold'>Status</th>
                    <th className='fw-bold'>Paid</th>
                    <th className='fw-bold'>Approved</th>
                    <th className='fw-bold'>Not Assigned</th>
                    <th className='fw-bold'>Pending</th>
                    <th className='fw-bold'>Active Claims</th>
                    <th className='fw-bold'>Created</th>
                    {isSuperAdmin && <th className='fw-bold'>Organization</th>}
                    <th className='fw-bold'>Created by</th>
                    {!isAgente && <th className='fw-bold'>Actions</th>}
                  </tr>
                </thead>
                {(loading || !dataAllCommissionReports) && !isPag ? (
                  <tbody>
                    <tr>
                      <td colSpan={10 + (isSuperAdmin ? 1 : 0) + (!isAgente ? 1 : 0)}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={10 + (isSuperAdmin ? 1 : 0) + (!isAgente ? 1 : 0)}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            There are no commission reports currently added
                          </div>
                        </td>
                      </tr>
                    )}

                    {loading && (
                      <tr>
                        <td colSpan={11}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <Spinner animation='border' />
                          </div>
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      Object.entries(groupByMonthYear(dataArr)).map(([monthYear, items]) => (
                        <React.Fragment key={monthYear}>
                          <tr>
                            <td
                              colSpan={10 + (isSuperAdmin ? 1 : 0) + (!isAgente ? 1 : 0)}
                              style={{
                                backgroundColor: '#337ab7',
                                color: 'black',
                                textAlign: 'left',
                                padding: '8px',
                              }}
                            >
                              {monthYear}
                            </td>
                          </tr>
                          {items.map(({node: nodeCR}) => (
                            <tr key={nodeCR.id}>
                              <td></td>
                              <td>
                                <div>
                                  <a
                                    className='text-primary fw-bold'
                                    style={{cursor: 'pointer', textDecoration: 'underline'}}
                                    data-tooltip-id={`myt2${parseId(nodeCR.id)}`}
                                    onClick={() => navigate(`/commissions/${nodeCR.id}/details`)}
                                  >
                                    {nodeCR.name}
                                  </a>
                                  <p className='m-0 p-0'>
                                    {moment(
                                      `${nodeCR.effectiveYear}-${nodeCR.effectiveMonth}-01`
                                    ).format('MMMM YYYY')}
                                  </p>
                                </div>
                              </td>
                              <td>
                                <BadgeStatus state={nodeCR.status} />
                              </td>
                              <td>
                                {nodeCR.paidDate ? (
                                  <>
                                    <i
                                      className='bi bi-check-circle-fill text-success me-1'
                                      title='Paid'
                                    ></i>
                                    {moment(nodeCR.paidDate).format('DD MMM, YY')}
                                  </>
                                ) : (
                                  <i
                                    className='bi bi-x-circle-fill text-danger'
                                    title='Not paid yet'
                                  ></i>
                                )}
                              </td>
                              <td>{parsePrice(nodeCR.totalApproved)}</td>
                              <td>{parsePrice(nodeCR.totalNotAssigned)}</td>
                              <td>{parsePrice(nodeCR.totalPending)}</td>
                              <td>
                                {nodeCR.totalActiveClaims > 0 ? (
                                  <span className='badge badge-danger'>
                                    {nodeCR.totalActiveClaims}
                                  </span>
                                ) : (
                                  <span className='badge badge-info'>0</span>
                                )}
                              </td>
                              <td>{moment(nodeCR.created).format('DD MMM, YY')}</td>
                              {isSuperAdmin && (
                                <td>
                                  {nodeCR.createdBy?.organization?.name || (
                                    <span className='text-muted'>No organization</span>
                                  )}
                                </td>
                              )}
                              <td>
                                {nodeCR.createdBy?.firstName} {nodeCR.createdBy?.lastName}
                              </td>
                              {!isAgente && (
                                <td>
                                  {canEditCommission && (
                                    <Dropdown as={ButtonGroup}>
                                    <Button
                                      variant='outline-primary'
                                      size='sm'
                                      style={{border: '1px solid #3e97ff'}}
                                    >
                                      Actions
                                    </Button>
                                    <Dropdown.Toggle
                                      split
                                      variant='outline-primary'
                                      size='sm'
                                      id='dropdown-custom-2'
                                      style={{border: '1px solid #3e97ff'}}
                                    />
                                    <Dropdown.Menu className='dropdown-menu-commissionReport'>
                                      {nodeCR.paidDate ? (
                                        <Dropdown.Item
                                          eventKey='mark-unpaid'
                                          onClick={() => {
                                            const cleanData = {
                                              ...removeKeysDeep(nodeCR, [
                                                '__typename',
                                                'user',
                                                'createdBy',
                                                'id',
                                                'created',
                                                'totalApproved',
                                                'totalPending',
                                                'totalNotAssigned',
                                                'totalActiveClaims',
                                              ]),
                                              carrier: nodeCR.carrier?.id
                                                ? parseId(nodeCR.carrier.id)
                                                : null,
                                              stateEU: nodeCR.stateEU?.id
                                                ? parseId(nodeCR.stateEU.id)
                                                : null,
                                              paidDate: null, // marcar como no pagado
                                            }
                                            handleUpdateCommissionReport(nodeCR.id, cleanData)
                                          }}
                                        >
                                          <i className='bi bi-cash' /> Mark as unpaid
                                        </Dropdown.Item>
                                      ) : (
                                        <Dropdown.Item
                                          eventKey='mark-paid'
                                          onClick={() => {
                                            const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
                                            const cleanData = {
                                              ...removeKeysDeep(nodeCR, [
                                                '__typename',
                                                'user',
                                                'createdBy',
                                                'id',
                                                'created',
                                                'totalApproved',
                                                'totalPending',
                                                'totalNotAssigned',
                                                'totalActiveClaims',
                                              ]),
                                              carrier: nodeCR.carrier?.id
                                                ? parseId(nodeCR.carrier.id)
                                                : null,
                                              stateEU: nodeCR.stateEU?.id
                                                ? parseId(nodeCR.stateEU.id)
                                                : null,
                                              paidDate: today, // marcar como pagado hoy
                                            }
                                            handleUpdateCommissionReport(nodeCR.id, cleanData)
                                          }}
                                        >
                                          <i className='bi bi-cash-stack' /> Mark as paid
                                        </Dropdown.Item>
                                      )}

                                      <Dropdown.Divider />
                                      <Dropdown.Item
                                        eventKey='details'
                                        onClick={() =>
                                          navigate(`/commissions/${nodeCR.id}/details`)
                                        }
                                      >
                                        <i className='bi bi-gear' /> Manage
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        eventKey='edit'
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setInfoEdit(nodeCR)
                                          setIsOpenEdit(true)
                                        }}
                                      >
                                        <i className='bi bi-pencil-square' /> Update
                                      </Dropdown.Item>

                                      {nodeCR.status === 'DRAFT' && (
                                        <Dropdown.Item
                                          eventKey='close'
                                          onClick={() => handleClickClose(nodeCR)}
                                        >
                                          <i className='bi bi-lock-fill' /> Close report
                                        </Dropdown.Item>
                                      )}
                                      {nodeCR.status === 'COMPLETED' && (
                                        <>
                                          <Dropdown.Item
                                            eventKey='deactivate'
                                            onClick={() => handleClickReopen(nodeCR)}
                                          >
                                            <i className='bi bi-unlock-fill' /> Reopen report
                                          </Dropdown.Item>
                                        </>
                                      )}
                                      {nodeCR.status === 'DRAFT' && (
                                        <>
                                          <Dropdown.Divider />
                                          <Dropdown.Item
                                            eventKey='activate'
                                            onClick={() => {
                                              onOpenDeleteCommissionReport()
                                              setInfoEdit(nodeCR)
                                            }}
                                          >
                                            <i className='bi bi-trash-fill' /> Delete
                                          </Dropdown.Item>
                                        </>
                                      )}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>
      {canEditCommission && (
        <>
          <Button
            style={floatingTabStyle}
            variant='light'
            title='Commissions Finder'
            onClick={onOpenSearchDetails}
          >
            <i className='bi bi-search fs-4' />
          </Button>

          <OffcanvasSearchCommissionDetails
            isOpen={isOpenSearchDetails}
            onClose={onCloseSearchDetails}
          />
        </>
      )}

      {isOpen && (
        <ModalAddCommissionReport
          refetch={refetch}
          isOpen={isOpen}
          onClose={onClose}
          keyboard={false}
          backdrop='static'
          idFormEdit={infoPreview}
        />
      )}
      {isOpenEdit && (
        <ModalEditCommissionReport
          isOpen={isOpenEdit}
          onClose={() => setIsOpenEdit(false)}
          refetch={refetch}
          infoEdit={infoEdit}
        />
      )}
      {isOpenDeleteCommissionReport && infoEdit && (
        <ModalDeleteCommissionReport
          isOpen={isOpenDeleteCommissionReport}
          onClose={onCloseDeleteCommissionReport}
          refetch={refetch}
          infoEdit={infoEdit}
        />
      )}
      {isOpenCloseModal && (
        <ModalConfirmation
          isOpen={isOpenCloseModal}
          onClose={() => setIsOpenCloseModal(false)}
          onConfirm={handleConfirmClose}
          refetch={refetch}
          title='Close Report'
          confirmText='Yes, Close Report'
          confirmVariant='danger'
          body={
            <>
              <p>
                <strong>Are you sure you wish to close this report?</strong>
              </p>
              <ul>
                <li>
                  Marked as <strong>NOT ASSIGNED</strong> the policies that should not be paid on
                  this report
                </li>
                <li>Updated/confirmed the rates to be paid per policy are correct</li>
                <li>
                  Understand that all the <strong>pending</strong> policies will be marked as{' '}
                  <strong>NOT ASSIGNED</strong>
                </li>
              </ul>
            </>
          }
        />
      )}

      {isOpenReopenModal && (
        <ModalConfirmation
          isOpen={isOpenReopenModal}
          onClose={() => setIsOpenReopenModal(false)}
          onConfirm={handleConfirmReopen}
          refetch={refetch}
          title='Reopen Report'
          confirmText='Yes, Reopen Report'
          confirmVariant='success'
          body={
            <p>
              <strong>Are you sure you wish to reopen this report?</strong>
            </p>
          }
        />
      )}
    </div>
  )
}
